import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sparkles, LayoutDashboard, LogOut } from "lucide-react";
import { clearUserSession, getUserToken } from "../utils/auth";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();
  const isUserLoggedIn = Boolean(getUserToken());
  const startAiPath = isUserLoggedIn ? "/contact" : "/login?redirect=/contact";

  const handleLogout = () => {
    clearUserSession();
    setIsMobileOpen(false);
    window.location.href = '/login';
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location]);

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/services", label: "Services" },
    { to: "/projects", label: "Projects" },
    { to: "/news", label: "News & Articles" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        isScrolled
          ? "py-2"
          : "py-0"
      }`}
    >
      {/* Glassmorphism backdrop */}
      <div
        className={`absolute inset-0 transition-all duration-700 ${
          isScrolled ? "opacity-100" : "opacity-0"
        }`}
        style={{
          background: "linear-gradient(135deg, rgba(15, 23, 42, 0.85), rgba(15, 23, 42, 0.75))",
          backdropFilter: "blur(24px) saturate(180%)",
          WebkitBackdropFilter: "blur(24px) saturate(180%)",
          borderBottom: "1px solid rgba(99, 102, 241, 0.12)",
          boxShadow: isScrolled
            ? "0 4px 30px rgba(0, 0, 0, 0.3), 0 0 40px rgba(99, 102, 241, 0.05)"
            : "none",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 no-underline group">
            <div className="relative">
              <div className="w-11 h-11 rounded-xl overflow-hidden bg-slate-900/80 flex items-center justify-center shadow-lg">
                <img src="/cc.png" alt="ClientCode logo" className="w-full h-full object-cover" />
              </div>
              <div
                className="absolute inset-0 rounded-xl bg-gradient-to-br from-accent to-ai-glow blur-xl transition-opacity duration-500"
                style={{ opacity: 0.4 }}
              />
              <div className="absolute -inset-1 rounded-xl bg-gradient-to-br from-accent to-ai-glow opacity-0 blur-2xl group-hover:opacity-60 transition-opacity duration-700" />
            </div>
            <div className="flex flex-col">
              <span className="text-lg font-bold text-white tracking-wide leading-tight">
                CLIENTCODE AI
              </span>
              <span className="text-[10px] font-semibold tracking-[0.25em] text-ai-glow uppercase">
                Innovation Pvt. Ltd.
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1 rounded-2xl px-2 py-1.5" style={{
            background: "rgba(15, 23, 42, 0.4)",
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(99, 102, 241, 0.06)",
          }}>
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                className={({ isActive }) =>
                  `relative px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 no-underline ${
                    isActive
                      ? "text-white"
                      : "text-slate-400 hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <motion.div
                        layoutId="activeNavIndicator"
                        className="absolute inset-0 rounded-xl"
                        style={{
                          background: "linear-gradient(135deg, rgba(99, 102, 241, 0.2), rgba(34, 211, 238, 0.1))",
                          border: "1px solid rgba(99, 102, 241, 0.25)",
                          boxShadow: "0 0 20px rgba(99, 102, 241, 0.15), inset 0 1px 0 rgba(255,255,255,0.05)",
                        }}
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    )}
                    <span className="relative z-10">{link.label}</span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* CTA + Mobile Toggle */}
          <div className="flex items-center gap-3">
            {isUserLoggedIn ? (
              <>
                <Link
                  to="/dashboard"
                  className="hidden sm:inline-flex neon-btn neon-btn-outline text-sm no-underline"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  Dashboard
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="hidden sm:inline-flex neon-btn neon-btn-primary text-sm"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </>
            ) : (
              <Link
                to={startAiPath}
                className="hidden sm:inline-flex neon-btn neon-btn-primary text-sm no-underline"
              >
                <Sparkles className="w-4 h-4" />
                Start AI Now
              </Link>
            )}

            <button
              onClick={() => setIsMobileOpen(!isMobileOpen)}
              className="lg:hidden p-2.5 rounded-xl text-slate-300 hover:text-white transition-all duration-300 bg-transparent border border-white/5 hover:border-accent/30 hover:bg-accent/5 cursor-pointer"
            >
              {isMobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
            className="lg:hidden overflow-hidden"
            style={{
              background: "linear-gradient(180deg, rgba(15, 23, 42, 0.97), rgba(15, 23, 42, 0.95))",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
              borderTop: "1px solid rgba(99, 102, 241, 0.08)",
            }}
          >
            <div className="px-5 py-5 space-y-1">
              {navLinks.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05, duration: 0.3 }}
                >
                  <NavLink
                    to={link.to}
                    end={link.to === "/"}
                    onClick={() => setIsMobileOpen(false)}
                    className={({ isActive }) =>
                      `block px-5 py-3.5 rounded-xl text-sm font-medium transition-all no-underline ${
                        isActive
                          ? "text-white bg-gradient-to-r from-accent/15 to-transparent border-l-2 border-accent"
                          : "text-slate-400 hover:text-white hover:bg-white/5"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.3 }}
                className="pt-3 space-y-2"
              >
                {isUserLoggedIn ? (
                  <>
                    <Link
                      to="/dashboard"
                      onClick={() => setIsMobileOpen(false)}
                      className="block w-full text-center neon-btn neon-btn-outline text-sm no-underline"
                    >
                      <LayoutDashboard className="w-4 h-4 inline mr-2" />
                      Dashboard
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="block w-full text-center neon-btn neon-btn-primary text-sm"
                    >
                      <LogOut className="w-4 h-4 inline mr-2" />
                      Logout
                    </button>
                  </>
                ) : (
                  <Link
                    to={startAiPath}
                    onClick={() => setIsMobileOpen(false)}
                    className="block w-full text-center neon-btn neon-btn-primary text-sm no-underline"
                  >
                    <Sparkles className="w-4 h-4 inline mr-2" />
                    Start AI Now
                  </Link>
                )}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
